import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "../hooks/use-toast";
import { ezugiApi } from "../lib/api";
import { useApp } from "../context/AppContext";

const FRESH_MS = 4 * 60 * 1000;  // hub launch tokens are single-use; re-launch if the prefetched one is older than this

// BetBy sportsbook (via the casino hub) opens as its own full page, exactly like a hub game card.
// The launch URL is prefetched while the player is on Exchange so the tap navigates instantly.
export function useSportsbookLauncher() {
  const { isAuthed } = useApp();
  const [enabled, setEnabled] = useState(true);
  const [opening, setOpening] = useState(false);
  const pre = useRef({ url: "", at: 0 });

  const fetchUrl = useCallback(async () => {
    const sb = await ezugiApi.sportsbook();
    if (!sb.enabled) { setEnabled(false); return ""; }
    const { launch_url } = await ezugiApi.launch(sb.gameId, "/sports");
    pre.current = { url: launch_url, at: Date.now() };
    return launch_url;
  }, []);

  useEffect(() => {  // prefetch + keep fresh
    if (!isAuthed) { pre.current = { url: "", at: 0 }; return; }
    fetchUrl().catch(() => {});
    const t = setInterval(() => fetchUrl().catch(() => {}), FRESH_MS);
    return () => clearInterval(t);
  }, [isAuthed, fetchUrl]);

  const open = useCallback(async () => {
    if (!isAuthed) { document.querySelector('[data-testid="nav-signin-btn"]')?.click(); return; }
    setOpening(true);
    const fresh = pre.current.url && Date.now() - pre.current.at < FRESH_MS;
    let url = fresh ? pre.current.url : "";
    pre.current = { url: "", at: 0 };  // consumed
    for (let attempt = 0; !url && attempt < 2; attempt++) {  // hub edge can drop a request — retry once before giving up
      try { url = await fetchUrl(); } catch (e) { if (attempt === 1) { setOpening(false); toast({ title: "Sportsbook is busy", description: e?.response?.data?.detail || "Please tap again in a moment.", variant: "destructive" }); return; } }
    }
    if (url) window.location.href = url; else setOpening(false);
  }, [isAuthed, fetchUrl]);

  return { open, opening, enabled };
}
